/**
 * CompanyChannelTreeController - grouped Company / Channel checkbox tree for Product Groups.
 *
 * Companies are parent rows; their channels are nested children. Selecting a company
 * (company-wide / "All channels") and selecting specific channels are mutually exclusive
 * within the same company: checking the company disables and clears its channels, and
 * checking any channel disables the company box. A search box filters and auto-expands
 * matches, a live count is shown, and submit is blocked when nothing is selected.
 */

import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['field', 'tree', 'search', 'count', 'error'];

  connect() {
    this.form = this.element.closest('form');
    if (this.form) {
      this.boundValidate = this.validate.bind(this);
      this.form.addEventListener('submit', this.boundValidate);
    }
    this.updateCount();
  }

  disconnect() {
    if (this.form && this.boundValidate) {
      this.form.removeEventListener('submit', this.boundValidate);
    }
  }

  toggleExpand(event) {
    const company = event.currentTarget.closest('.cc-tree__company');
    const channels = company.querySelector('.cc-tree__channels');
    const caret = company.querySelector('.cc-tree__caret');
    const collapsed = channels.hidden;
    channels.hidden = !collapsed;
    caret.textContent = collapsed ? '▼' : '▶';
  }

  toggleCompany(event) {
    const box = event.currentTarget;
    const companyId = box.dataset.companyId;
    const channelBoxes = this.#channelBoxesFor(companyId);

    channelBoxes.forEach((channel) => {
      channel.checked = false;
      channel.disabled = box.checked;
      channel.closest('.cc-tree__channel').classList.toggle('is-disabled', box.checked);
    });
    this.#allBadgeFor(companyId).hidden = !box.checked;
    this.updateCount();
  }

  toggleChannel(event) {
    const companyId = event.currentTarget.dataset.companyId;
    const companyBox = this.#companyBoxFor(companyId);
    const anyChecked = this.#channelBoxesFor(companyId).some((channel) => channel.checked);

    companyBox.disabled = anyChecked;
    companyBox.closest('.cc-tree__label').classList.toggle('is-disabled', anyChecked);
    this.updateCount();
  }

  filter() {
    const query = this.searchTarget.value.toLowerCase().trim();

    this.treeTarget.querySelectorAll('.cc-tree__company').forEach((company) => {
      const companyText = company.dataset.searchText || '';
      const companyMatch = !query || companyText.includes(query);
      const channels = company.querySelector('.cc-tree__channels');
      let anyChannelVisible = false;

      company.querySelectorAll('.cc-tree__channel').forEach((channel) => {
        const channelText = channel.dataset.searchText || '';
        const match = !query || companyMatch || channelText.includes(query);
        channel.hidden = !match;
        if (match) anyChannelVisible = true;
      });

      const visible = companyMatch || anyChannelVisible;
      company.hidden = !visible;
      if (query && visible) {
        channels.hidden = false;
        company.querySelector('.cc-tree__caret').textContent = '▼';
      }
    });
  }

  updateCount() {
    const total = this.#selectedBoxes().length;
    this.countTarget.textContent = `${total} selected`;
    if (this.hasErrorTarget && total > 0) {
      this.errorTarget.hidden = true;
      this.fieldTarget.classList.remove('is-invalid');
    }
  }

  validate(event) {
    if (this.#selectedBoxes().length > 0) return;

    event.preventDefault();
    this.errorTarget.hidden = false;
    this.fieldTarget.classList.add('is-invalid');
    this.fieldTarget.scrollIntoView({ block: 'center' });
  }

  #selectedBoxes() {
    return Array.from(
      this.treeTarget.querySelectorAll('input[type=checkbox]:checked:not(:disabled)')
    );
  }

  #channelBoxesFor(companyId) {
    return Array.from(
      this.treeTarget.querySelectorAll(`input[data-cc-channel-box][data-company-id="${companyId}"]`)
    );
  }

  #companyBoxFor(companyId) {
    return this.treeTarget.querySelector(`input[data-cc-company-box][data-company-id="${companyId}"]`);
  }

  #allBadgeFor(companyId) {
    return this.treeTarget.querySelector(`.cc-tree__all-badge[data-company-id="${companyId}"]`);
  }
}
